import React, { useState } from 'react';
import { Box } from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid-premium';
import { useRouter } from 'next/router';
import {
  BookingsGrid,
  BookingsFilters,
  BookingsHeaderOptions,
} from '@components';
import {
  GridToolbar,
  TablePagination,
  KanbanBoard,
  KanbanCardInfoMenu,
} from 'components/common/grid';
import {
  useColumnVisibility,
  useDataGridModel,
  useKanbanVisibility,
  useViewportGridHeight,
  useKanbanCardInfo,
} from 'hooks';
import { URLS } from 'layouts/DrawerLayout/constants';
import { BOOKINGS_PAGE_SIZE } from 'hooks/bookings/useBookingsOperations';
import { generateSearchPlaceholder, getUrl } from '@utils';
import { BOOKINGS_FIELDS_TO_SEARCH } from 'constants/fieldsToSearch';
import { FiltersModalV2 } from 'components/common/modals';
import { useBookingsFilterConfig } from 'hooks/bookings';
import { VIEW_MODES } from '@constants';
import { BOOKINGS_KANBAN_CARD_INFO } from './kanbanCardInfo';
import styles from './bookingsListContent.style';

export const bookingsInitialGridColumns = [
  { value: true, label: 'ID', key: 'id' },
  { value: true, label: 'Stage', key: 'stage' },
  { value: true, label: 'Customer Name', key: 'fullName' },
  { value: true, label: 'Created At', key: 'created_at' },
  { value: true, label: 'Origin', key: 'origin' },
  { value: true, label: 'Contract Type', key: 'contractType' },
  { value: true, label: 'Contract Period', key: 'contractPeriod' },
  { value: true, label: 'Labels', key: 'bookingLabels' },
  { value: true, label: 'Customer Types', key: 'customerTypes' },
  { value: true, label: 'Holding Company', key: 'tenant' },
  { value: true, label: 'Email', key: 'email' },
  {
    value: true,
    label: 'User ID',
    key: 'userInternalId',
  },
  { value: true, label: 'Phone Number', key: 'phoneNumber' },
  { value: true, label: 'SKU ID', key: 'skuId' },
  { value: true, label: 'Vehicle', key: 'vehicle' },
  { value: true, label: 'Pipedrive ID', key: 'pipeDriveId' },
  { value: true, label: 'Price', key: 'fee' },
  { value: true, label: 'Assignee', key: 'assignee' },
  { value: true, label: 'Contract Value', key: 'contractValue' },
  { value: true, label: 'Won Time', key: 'wonTime' },
];

const BookingsListContent = ({
  total,
  loading,
  bookings,
  columns,
  assignees,
  page,
  pageSize,
  enums,
  customerTypesOptions,
  addSnapshot,
  clearSnapshot,
  bookingSnapshot,
  bookingLabelsOptions,
  filters,
  onGetBookings,
  onChangeRules,
  onFiltersModalChange,
  customFields,
}) => {
  const apiRef = useGridApiRef();
  const router = useRouter();
  const [viewMode, setViewMode] = useState(
    router.query.viewMode || VIEW_MODES.list.value
  );
  const { handleChangeVisibility, gridColumns } = useColumnVisibility(
    apiRef,
    bookingsInitialGridColumns,
    loading
  );
  const {
    handleKanbanVisibility,
    kanbanColumns,
    kanbanFilteredColumns,
    kanbanHiddenColumns,
  } = useKanbanVisibility(columns);

  const { cardFields, handleToggleCardField } = useKanbanCardInfo(
    BOOKINGS_KANBAN_CARD_INFO
  );

  const filterConfig = useBookingsFilterConfig({
    customerTypesOptions,
    bookingLabelsOptions,
    assignees,
  });

  const { handlePageChange } = useDataGridModel({
    url: URLS.bookings,
    page,
    pageSize,
    defaultPageSize: BOOKINGS_PAGE_SIZE,
  });

  const handleColumnVisibility = (column) => {
    if (viewMode === VIEW_MODES.list.value) {
      handleChangeVisibility(column);
    } else {
      handleKanbanVisibility(column);
    }
  };

  const { gridHeight } = useViewportGridHeight({
    customOffset: filterConfig.hasFilters ? 320 : 240,
  });

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    router.push({
      pathname: router.pathname,
      query: { ...router.query, viewMode: mode },
    });
  };

  const handleSubmitRules = (rules) => {
    router.push(getUrl({ ...router.query, page: '1' }, URLS.bookings));
    onChangeRules(rules);
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <BookingsFilters
          assignees={assignees}
          customerTypesOptions={customerTypesOptions}
          bookingLabelsOptions={bookingLabelsOptions}
        />
      </Box>
      {viewMode === VIEW_MODES.list.value ? (
        <BookingsGrid
          hasFilters={filterConfig.hasFilters}
          apiRef={apiRef}
          loading={loading}
          bookings={bookings}
          assignees={assignees}
          getBookings={onGetBookings}
          page={page}
          pageSize={pageSize}
          total={total}
          slots={{ toolbar: GridToolbar }}
          slotProps={{
            toolbar: {
              onRefresh: onGetBookings,
              gridColumns,
              filterConfig,
              initialGridColumns: bookingsInitialGridColumns,
              selectedViewMode: viewMode,
              searchPlaceholder: generateSearchPlaceholder(
                enums?.searchableFields || BOOKINGS_FIELDS_TO_SEARCH
              ),
              viewModes: VIEW_MODES,
              onChangeColumnVisibility: handleColumnVisibility,
              onViewModeChange: handleViewModeChange,
              extraToolbarItems: (
                <FiltersModalV2
                  isGridAction
                  onSubmit={handleSubmitRules}
                  initialState={filters.modalConditions}
                  fields={customFields}
                  onStateChange={onFiltersModalChange}
                />
              ),
              addItemComponent: (
                <BookingsHeaderOptions
                  loading={loading}
                  bookingLabelsOptions={bookingLabelsOptions}
                  bookingSnapshot={bookingSnapshot}
                  onGetBookings={onGetBookings}
                  onClearSnapshot={clearSnapshot}
                  onAddSnapshot={addSnapshot}
                />
              ),
            },
          }}
        />
      ) : (
        <Box sx={styles.container}>
          <GridToolbar
            filterConfig={filterConfig}
            selectedViewMode={viewMode}
            searchPlaceholder={generateSearchPlaceholder(
              enums?.searchableFields
            )}
            viewModes={VIEW_MODES}
            onRefresh={onGetBookings}
            onViewModeChange={handleViewModeChange}
            gridColumns={kanbanColumns}
            initialGridColumns={columns}
            onChangeColumnVisibility={handleColumnVisibility}
            extraToolbarItems={
              <KanbanCardInfoMenu
                items={cardFields || []}
                initialItems={BOOKINGS_KANBAN_CARD_INFO}
                onToggle={handleToggleCardField}
              />
            }
            addItemComponent={
              <BookingsHeaderOptions
                loading={loading}
                bookingLabelsOptions={bookingLabelsOptions}
                bookingSnapshot={bookingSnapshot}
                onGetBookings={onGetBookings}
                onClearSnapshot={clearSnapshot}
                onAddSnapshot={addSnapshot}
              />
            }
          />
          <Box sx={{ height: gridHeight }}>
            <KanbanBoard
              columns={kanbanFilteredColumns || []}
              hiddenColumns={kanbanHiddenColumns || []}
              cardFields={cardFields || []}
            />
          </Box>
          <TablePagination
            total={total}
            page={page}
            pageSize={pageSize}
            onPageChange={(newPage) => handlePageChange(newPage)}
            onPageSizeChange={(newSize) => handlePageChange(1, newSize)}
          />
        </Box>
      )}
    </>
  );
};

export default BookingsListContent;
